/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import * as Sentry from '@sentry/react';
import type { HostedFields } from 'braintree-web';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import { VisuallyHidden } from '@coursera/cds-core';
import { color } from '@coursera/coursera-ui';
import { SvgLock } from '@coursera/coursera-ui/svg';

import {
  clearBraintreeHostedFields,
  setBraintreeClientToken,
  setBraintreeHostedFields,
  setCreditCardData,
  updateCountry,
  updateZipcode,
} from 'bundles/account-settings/actions/EnterCardFormActions';
import BraintreeHostedFieldFormGroup from 'bundles/account-settings/components/sections/payment-method/BraintreeHostedFieldFormGroupController';
import type EnterCardFormStoreType from 'bundles/account-settings/stores/EnterCardFormStore';
import LoadingIcon from 'bundles/courseraLoadingIcon/LoadingIcon';
import Naptime from 'bundles/naptimejs';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import PaymentProcessorClientTokensV1 from 'bundles/naptimejs/resources/paymentProcessorClientTokens.v1';
import { conditionallyCreateBraintreeHostedFields } from 'bundles/payments-common/utils/braintreeClientUtils';
import InputFormGroup from 'bundles/payments/components/inputs/InputFormGroup';
import LocationFormGroup from 'bundles/payments/components/inputs/LocationFormGroup';

import _t from 'i18n!nls/payments';

import 'css!./__styles__/EnterCardForm';

type PropsFromCaller = {
  isSubmitted: boolean;
};

type PropsFromNaptime = {
  braintreeClientTokenResponse: PaymentProcessorClientTokensV1;
};

type PropsFromStores = {
  creditCardData: {
    cardholderName: string;
    ccNumber: string;
    expirationMonth: string;
    expirationYear: string;
    cvv: string;
  };
  activeWalletId: number;
  billingCountry?: string;
  billingZipcode?: string;
  braintreeHostedFields?: HostedFields;
};

type PropsToComponent = PropsFromCaller & PropsFromNaptime & PropsFromStores;

const styles = {
  formContainer: css`
    .bt3-form-horizontal .bt3-form-group::before,
    .bt3-form-horizontal .bt3-form-group::after {
      content: ' ';
      display: table;
    }

    .bt3-form-horizontal .bt3-form-group::after {
      clear: both;
    }

    .bt3-form-horizontal .bt3-radio {
      margin-top: 0;
      margin-bottom: 0;
      padding-top: 7px;
      min-height: 27px;
    }

    .bt3-form-horizontal .bt3-form-group {
      margin-left: -15px;
      margin-right: -15px;
    }
  `,
};

class EnterCardForm extends React.Component<PropsToComponent> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof EnterCardForm.contextTypes>;

  componentDidMount() {
    const { executeAction } = this.context;
    const { braintreeClientTokenResponse } = this.props;

    const braintreeClientToken = braintreeClientTokenResponse.definition.clientToken;
    executeAction(setBraintreeClientToken, { braintreeClientToken });
    this.conditionallyCreateBraintreeHostedFields();
  }

  componentDidUpdate(prevProps: Readonly<PropsToComponent>): void {
    const { braintreeClientTokenResponse } = this.props;
    const braintreeClientToken = braintreeClientTokenResponse.definition.clientToken;
    const { braintreeClientTokenResponse: prevBraintreeClientTokenResponse } = prevProps;
    const prevBraintreeClientToken = prevBraintreeClientTokenResponse.definition.clientToken;
    if (prevBraintreeClientToken !== braintreeClientToken) {
      this.conditionallyCreateBraintreeHostedFields();
    }
  }

  componentWillUnmount(): void {
    const { braintreeHostedFields } = this.props;
    if (braintreeHostedFields) {
      const { executeAction } = this.context;
      executeAction(clearBraintreeHostedFields);
      braintreeHostedFields.teardown();
    }
  }

  onUpdateName = (cardholderName: $TSFixMe) => {
    const { executeAction } = this.context;
    executeAction(setCreditCardData, { cardholderName });
  };

  onUpdateCCNumber = (ccNumber: $TSFixMe) => {
    const { executeAction } = this.context;
    executeAction(setCreditCardData, { ccNumber });
  };

  onUpdateMonth = (expirationMonth: $TSFixMe) => {
    const { executeAction } = this.context;
    executeAction(setCreditCardData, { expirationMonth });
  };

  onUpdateYear = (expirationYear: $TSFixMe) => {
    const { executeAction } = this.context;
    executeAction(setCreditCardData, { expirationYear });
  };

  onUpdateCVV = (cvv: $TSFixMe) => {
    const { executeAction } = this.context;
    executeAction(setCreditCardData, { cvv });
  };

  onUpdateAddress = (fieldName: string, value?: string | null) => {
    const { executeAction } = this.context;
    switch (fieldName) {
      case 'country':
        executeAction(updateCountry, { country: value });
        break;
      case 'zipcode':
      case 'postalCode':
        executeAction(updateZipcode, { zipcode: value });
        break;
      default:
        Sentry.captureMessage(`Unsupported address field in EnterCardForm: ${fieldName}`, {
          level: 'warning',
        });
    }
  };

  getBraintreeClientToken = () => this.props.braintreeClientTokenResponse.definition.clientToken;

  conditionallyCreateBraintreeHostedFields() {
    const styles = {
      input: {
        color: '#373a3c',
        'font-size': '1em',
        'font-family':
          'OpenSans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      },
      'input::placeholder': {
        color: '#757575',
        opacity: 1,
      },
      '.invalid, .blurry, .form-submitted': {
        color: '#D30001',
      },
      '.valid, .blurry.valid, .form-submitted.valid': {
        color: '#1F8354',
      },
    };
    const { braintreeHostedFields } = this.props;
    conditionallyCreateBraintreeHostedFields(this.getBraintreeClientToken, styles, braintreeHostedFields).then(
      (hostedFields) => {
        if (hostedFields) {
          const { executeAction } = this.context;
          executeAction(setBraintreeHostedFields, { braintreeHostedFields: hostedFields });
        }
      }
    );
  }

  static LOADING_COMPONENT = () => (
    <div>
      <VisuallyHidden role="status" aria-live="polite">
        {_t('Loading payment settings')}
      </VisuallyHidden>
      <LoadingIcon />
    </div>
  );

  render() {
    const { creditCardData, isSubmitted, activeWalletId, billingCountry, billingZipcode } = this.props;
    const { cardholderName } = creditCardData;
    const isTabLayoutForm = false;
    const onValidate = null;

    const nameInput = (
      <InputFormGroup
        labelText={_t('Name on card')}
        htmlAttributes={{ 'data-e2e': 'cc-name' }}
        placeholder={_t('Enter the name on your card')}
        name="ccFullName"
        onChange={this.onUpdateName}
        validateFn={(name) => name.length > 1}
        defaultValue={cardholderName}
        isFormSubmitted={isSubmitted}
        isVerticallyAligned={isTabLayoutForm}
        inputId="cc-name"
        isRequired={true}
      />
    );

    const cardNumberInput = (
      <BraintreeHostedFieldFormGroup
        braintreeFieldName="number"
        labelText={_t('Card Number')}
        isFormSubmitted={isSubmitted}
        isVerticallyAligned={isTabLayoutForm}
      />
    );

    const cardNumberInputWithLockIcon = (
      <div className="card-number-input-container">
        {cardNumberInput}
        {isTabLayoutForm && (
          <SvgLock size={22} color={color.disabled} style={{ position: 'absolute', right: '12px', bottom: '14px' }} />
        )}
      </div>
    );

    const expirationInputs = (
      <BraintreeHostedFieldFormGroup
        braintreeFieldName="expirationDate"
        labelText={_t('Expiration Date')}
        isFormSubmitted={isSubmitted}
        isVerticallyAligned={isTabLayoutForm}
        cols="4"
      />
    );

    const cvvInput = (
      <BraintreeHostedFieldFormGroup
        braintreeFieldName="cvv"
        labelText="CVV"
        cols="2"
        isFormSubmitted={isSubmitted}
        isVerticallyAligned={isTabLayoutForm}
      />
    );

    const nameInputClasses = classNames('bt3-col-sm-offset-1 no-gutters', {
      'no-margin bt3-col-sm-12': isTabLayoutForm,
      'bt3-col-sm-10': !isTabLayoutForm,
    });

    const cardNumberInputClasses = classNames('bt3-col-sm-offset-1 no-gutters', {
      'no-margin bt3-col-sm-7': isTabLayoutForm,
      'bt3-col-sm-5': !isTabLayoutForm,
    });

    return (
      <div css={styles.formContainer} className="rc-EnterCardForm">
        <p>{_t('All fields are required unless marked as optional.')}</p>
        <form id="coursera-payment-braintree-form" className="bt3-form-horizontal">
          {isTabLayoutForm ? (
            <div>
              {!activeWalletId && (
                <div>
                  <div className="bt3-row no-margin">
                    <div className={nameInputClasses}>{nameInput}</div>
                  </div>
                  <div className="bt3-row no-margin">
                    <div className={cardNumberInputClasses}>{cardNumberInputWithLockIcon}</div>
                    <div className="bt3-col-sm-3 bt3-col-xs-6 no-gutters expiration-input">{expirationInputs}</div>
                    <div className="bt3-col-sm-2 bt3-col-xs-6 no-gutters cvv-input cvv-input-container">{cvvInput}</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              {nameInput}
              {cardNumberInputWithLockIcon}
              {expirationInputs}
              {cvvInput}
            </div>
          )}

          <p id="cvv-description" className="sr-only">
            {_t("Enter your credit card's three or four-digit security code.")}
          </p>

          {!(isTabLayoutForm && activeWalletId) && (
            <LocationFormGroup
              address={{
                country: billingCountry,
                zipcode: billingZipcode,
              }}
              isFormSubmitted={isSubmitted}
              isTabLayout={isTabLayoutForm}
              onValidate={onValidate}
              shouldAlwaysTriggerChange={true}
              onAddressChange={this.onUpdateAddress}
            />
          )}
        </form>
      </div>
    );
  }
}

type Stores = {
  EnterCardFormStore: typeof EnterCardFormStoreType;
};

const StoreConnectedEnterCardForm = connectToStores<PropsToComponent, PropsFromCaller & PropsFromNaptime, Stores>(
  ['EnterCardFormStore'],
  ({ EnterCardFormStore }) => {
    const { billingCountry, billingZipcode } = EnterCardFormStore.getBillingInformation() || {};
    const braintreeHostedFields = EnterCardFormStore.getBraintreeHostedFields();
    return {
      creditCardData: EnterCardFormStore.getCreditCardData(),
      activeWalletId: EnterCardFormStore.getPaymentWalletId(),
      billingCountry,
      billingZipcode,
      braintreeHostedFields,
    };
  }
)(EnterCardForm);

export default Naptime.createContainer<PropsFromCaller & PropsFromNaptime, PropsFromCaller>(
  StoreConnectedEnterCardForm,
  () => ({
    braintreeClientTokenResponse: PaymentProcessorClientTokensV1.get('braintree'),
  })
);
